import type { ChatStatus } from 'ai'
import { cn } from 'lib/utils'
import { CornerDownLeftIcon, SquareIcon } from 'lucide-react'
import { Button } from 'primitives/button'
import { Textarea } from 'primitives/textarea'
import type {
  ComponentProps,
  FormEvent,
  FormEventHandler,
  HTMLAttributes,
  KeyboardEventHandler,
} from 'react'
import { Children, forwardRef, useCallback, useRef, useState } from 'react'

// ============================================================================
// PromptInput (form wrapper)
// ============================================================================

export type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  onSubmit: (message: { text: string }, event: FormEvent<HTMLFormElement>) => void
}

export const PromptInput = ({ className, onSubmit, children, ...props }: PromptInputProps) => {
  const formRef = useRef<HTMLFormElement | null>(null)

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault()
      const formData = new FormData(event.currentTarget)
      const text = (formData.get('message') as string) || ''
      event.currentTarget.reset()
      onSubmit({ text }, event)
    },
    [onSubmit],
  )

  return (
    <form
      className={cn('flex flex-col rounded-lg border overflow-hidden', className)}
      onSubmit={handleSubmit}
      ref={formRef}
      {...props}
    >
      {children}
    </form>
  )
}

// ============================================================================
// PromptInputTextarea
// ============================================================================

export type PromptInputTextareaProps = ComponentProps<typeof Textarea>

export const PromptInputTextarea = forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  ({ onKeyDown, className, placeholder = 'What would you like to know?', ...props }, ref) => {
    const [isComposing, setIsComposing] = useState(false)

    const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
      (e) => {
        onKeyDown?.(e)
        if (e.defaultPrevented) return

        if (e.key === 'Enter') {
          if (isComposing || e.nativeEvent.isComposing) return
          if (e.shiftKey) return
          e.preventDefault()

          const { form } = e.currentTarget
          const submitButton = form?.querySelector(
            'button[type="submit"]',
          ) as HTMLButtonElement | null
          if (!submitButton || submitButton.disabled) return

          form?.requestSubmit()
        }
      },
      [onKeyDown, isComposing],
    )

    const handleInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 192)}px`
    }, [])

    return (
      <Textarea
        ref={ref}
        className={cn(
          'max-h-48 min-h-16 resize-none border-0 p-3 shadow-none focus-visible:ring-0',
          className,
        )}
        name="message"
        onCompositionEnd={() => setIsComposing(false)}
        onCompositionStart={() => setIsComposing(true)}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={1}
        {...props}
      />
    )
  },
)
PromptInputTextarea.displayName = 'PromptInputTextarea'

// ============================================================================
// PromptInputFooter
// ============================================================================

export type PromptInputFooterProps = HTMLAttributes<HTMLDivElement>

export const PromptInputFooter = ({ className, ...props }: PromptInputFooterProps) => (
  <div className={cn('flex items-center justify-between gap-1 px-3 py-2', className)} {...props} />
)

// ============================================================================
// PromptInputButton
// ============================================================================

export type PromptInputButtonProps = ComponentProps<typeof Button>

export const PromptInputButton = ({
  variant = 'ghost',
  className,
  size,
  ...props
}: PromptInputButtonProps) => {
  const newSize = size ?? (Children.count(props.children) > 1 ? 'sm' : 'iconSm')
  return (
    <Button className={cn(className)} size={newSize} type="button" variant={variant} {...props} />
  )
}

// ============================================================================
// PromptInputSubmit
// ============================================================================

export type PromptInputSubmitProps = ComponentProps<typeof Button> & {
  status?: ChatStatus
  onStop?: () => void
}

export const PromptInputSubmit = ({
  className,
  variant = 'default',
  size = 'iconSm',
  status,
  onStop,
  onClick,
  children,
  ...props
}: PromptInputSubmitProps) => {
  const isGenerating = status === 'submitted' || status === 'streaming'

  let Icon = <CornerDownLeftIcon className="size-4" />
  if (status === 'streaming') {
    Icon = <SquareIcon className="size-4" />
  }

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isGenerating && onStop) {
        e.preventDefault()
        onStop()
        return
      }
      onClick?.(e)
    },
    [isGenerating, onStop, onClick],
  )

  return (
    <Button
      aria-label={isGenerating ? 'Stop' : 'Submit'}
      className={cn(className)}
      onClick={handleClick}
      size={size}
      type={isGenerating && onStop ? 'button' : 'submit'}
      variant={variant}
      {...props}
    >
      {children ?? Icon}
    </Button>
  )
}
